export interface CircleSegment {
  name: string;
  color: string;
  value: number;
}

// Same fade a highlighted legend entry applies elsewhere (LinePlot) - see
// its own DIMMED comment.
const DIMMED = 0.2;

// "All signals" view - a donut with one arc per signal type, sized by its
// share of the total. Each arc's stroke-dasharray shows only its own
// fraction of the circumference; stroke-dashoffset (negative, accumulating
// the prior segments' fractions) advances the start point so segments sit
// end-to-end instead of overlapping.
export function MultiCircularMetric({ series, size = 170, highlightKey = null }: { series: CircleSegment[]; size?: number; highlightKey?: string | null }) {
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const total = series.reduce((sum, s) => sum + s.value, 0) || 1;

  let cumulativeFraction = 0;
  const segments = series
    ?.filter((s) => s.value > 0)
    ?.map((s) => {
      const fraction = s.value / total;
      const dash = circumference * fraction;
      const offset = -cumulativeFraction * circumference;
      cumulativeFraction += fraction;
      return { ...s, dash, offset };
    });

  return (
    <div className="mx-auto" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" width={size} height={size}>
        <circle cx="50" cy="50" r={radius} fill="none" stroke="currentColor" className="text-black/10 dark:text-white/10" strokeWidth={9} />
        {segments?.map((s) => (
          <circle
            key={s.name}
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={s.color}
            strokeWidth={highlightKey === s.name ? 11 : 9}
            strokeDasharray={`${s.dash} ${circumference - s.dash}`}
            strokeDashoffset={s.offset}
            opacity={highlightKey != null && s.name !== highlightKey ? DIMMED : 1}
            transform="rotate(-90 50 50)"
          />
        ))}
        <text x="50" y="55" textAnchor="middle" fontSize="18" fontWeight="700" fill="currentColor">
          {total > 0 ? series.reduce((sum, s) => sum + s.value, 0) : 0}
        </text>
      </svg>
    </div>
  );
}
